import { Image } from "@heroui/react";
import { FocusScope } from "@react-aria/focus";
import clsx from "clsx";
import { APP_CLINIC, DOCUMENT_PRIVACY_POLICY } from "core/consts";
import {
  ENV_DEMO,
  ENV_PREPROD,
  ENV_PRODUCTION,
  ENV_STAGING,
  getStaticAsset,
  isAcpApp,
  isDemoEnv,
  isPreprodEnv,
  isProdEnv,
  isReceiverApp,
  isSenderApp,
} from "core/model/config";
import {
  activateEnvSwitch,
  isDeveloperMode,
} from "core/model/utils/featureFlags";
import { capitaliseFirst } from "core/model/utils/strings";
import { Env } from "core/types";
import { useExternalLinkLabel } from "ds_legacy/components/LinkV2";
import { getRecareLogoWithNamePath } from "ds_legacy/components/RecareLogo";
import { Banner, Button, Form, FormProps, Link } from "ds/ui";
import { useLegalDocuments } from "dsl/atoms/LegalDocuments";
import { setCurrentAppOptions } from "dsl/ecosystems/CareproviderOnboardingLoginPage/LoginSuggestions";
import {
  LoginErrorState,
  LoginView,
} from "dsl/ecosystems/CareproviderOnboardingLoginPage/useHandleLogin";
import { useFocusElement } from "dsl/hooks/useFocusElement";
import { ArrowLeftIcon } from "lucide-react";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "translations";
import { loginPage, LoginPageVariants } from "./classes";

export type LoginPageLayoutProps = LoginPageVariants & {
  /**
   * Autofocus strategy. `"container"` focuses the first focusable element on
   * mount (legacy connected page). `"back-button"` focuses the challenge-view
   * back button via `FocusScope` (RHF forms). Omit for no autofocus.
   */
  autoFocus?: "back-button" | "container";
  challengeField: ReactNode;
  challengeTimestamp: string | undefined;
  contactUsUrl?: string;
  emailField: ReactNode;
  env: Env;
  goToForgotPassword: string;
  goToProductionUrl: string;
  isLoading: boolean;
  isSingleSignOn?: boolean;
  loginErrors?: LoginErrorState;
  loginView: LoginView;
  onBackToLogin: () => void;
  /**
   * When provided, the form region is an HTML `<form>` submitting via this
   * handler (RHF forms). When omitted, it is a plain `<div>` and the submit
   * button drives submission itself (legacy connected page nested in its own
   * form wrapper).
   */
  onSubmit?: FormProps["onSubmit"];
  otherAppUrl?: string;
  passwordField: ReactNode;
  /** Optional SSO info block, rendered between the email and password fields. */
  ssoInfo?: ReactNode;
  submit: ReactNode;
};

const ENVIRONMENTS = [
  ENV_STAGING,
  ENV_PREPROD,
  ENV_DEMO,
  ENV_PRODUCTION,
] as const;

/**
 * Presentational shell for the login page (header, dev-tool env switcher, title,
 * banners, footer). The form mechanism is left to the consumer: pass the input
 * nodes and submit button as slots, and either wire `onSubmit` (RHF) or let the
 * submit button submit a surrounding form (legacy connected page). Shared by
 * `LoginPageV2` and the RHF login forms so the page isn't recreated per app.
 */
export function LoginPageLayout({
  app = APP_CLINIC,
  autoFocus,
  challengeField,
  challengeTimestamp,
  contactUsUrl,
  emailField,
  env,
  goToForgotPassword,
  goToProductionUrl,
  isLoading,
  isSingleSignOn,
  loginErrors,
  loginView = "login",
  onBackToLogin,
  onSubmit,
  otherAppUrl,
  passwordField,
  ssoInfo,
  submit,
}: LoginPageLayoutProps) {
  const translations = useTranslations();
  const { getDocumentUrl } = useLegalDocuments();
  const externalLinkLabel = useExternalLinkLabel();
  const navigate = useNavigate();
  const classes = loginPage({ app, env });
  const appOptions = setCurrentAppOptions({ currentApp: app, env });

  const isDemo = isDemoEnv(env);
  const isProd = isProdEnv(env);
  const isPreprod = isPreprodEnv(env);
  const isLogin = loginView === "login";
  const isChallenge = loginView === "challenge";

  const focusRef = useFocusElement<HTMLDivElement>({
    focusFirstInnerElement: true,
    shouldFocus: autoFocus === "container",
  });

  const backButton = (
    <Button
      color="primary"
      id="back_to_login"
      size="sm"
      startContent={<ArrowLeftIcon />}
      variant="light"
      onPress={onBackToLogin}
    >
      {translations.actions.back}
    </Button>
  );

  const formContent = (
    <>
      <div className={clsx(classes.formContent(), "w-full")}>
        {isChallenge &&
          (autoFocus === "back-button" ? (
            <div>
              <FocusScope autoFocus>{backButton}</FocusScope>
            </div>
          ) : (
            backButton
          ))}
        <h1 className={classes.title()}>
          {(isPreprod || isProd) && isAcpApp(app) && (
            <span className="font-normal">
              {isProd
                ? translations.acp.login.prefixProd
                : translations.acp.login.prefixPreprod}{" "}
              |{" "}
            </span>
          )}
          {isDemo && (
            <span className="font-normal">
              {translations.login.loginPage.titleDemo} |{" "}
            </span>
          )}
          {isLogin
            ? isSenderApp(app)
              ? translations.login.loginPage.titleSender
              : isReceiverApp(app)
              ? translations.login.loginPage.titleReceiver
              : translations.login.loginPage.titleAcp
            : translations.login.loginPage.titleAccountVerification}
        </h1>
        {isChallenge && (
          <p className="text-sm">
            {translations.login.loginPage.accountVerificationInfo}
          </p>
        )}
        {isDemo && (
          <Banner
            color="warning"
            message={
              <p>
                {translations.login.loginPage.environmentInfo}{" "}
                <Link
                  aria-label={externalLinkLabel(
                    translations.login.loginPage.productionLink,
                    "website",
                  )}
                  color="primary-dark"
                  href={goToProductionUrl}
                  id="go-to-prod"
                  size="sm"
                >
                  {translations.login.loginPage.productionLink}
                </Link>
                .
              </p>
            }
            size="sm"
          />
        )}
      </div>
      <div className={clsx(classes.formContent(), "w-full")}>
        {loginErrors?.general && (
          <Banner
            color="danger"
            data-testid="login-error"
            message={loginErrors.general}
            role="alert"
          />
        )}
        {isChallenge && challengeTimestamp && (
          <p className="text-sm">
            {translations.login.challengeScreen.infoTextTimestamp({
              datetimestring: challengeTimestamp,
            })}
          </p>
        )}
        {emailField}
        {ssoInfo}
        {passwordField}
        {challengeField}
        {isLogin && !isSingleSignOn && (
          <p>
            <Link
              color="primary-dark"
              href={goToForgotPassword}
              id="forgot-password"
              size="sm"
            >
              {translations.login.loginPage.forgotPassword}
            </Link>
          </p>
        )}
      </div>
      <div className="flex w-full justify-between">{submit}</div>
    </>
  );

  return (
    <div className={classes.page()}>
      {isDeveloperMode && (
        <div className={classes.devToolContainer()}>
          {activateEnvSwitch && (
            <div
              className={clsx(
                classes.envSwitcher(),
                "rounded-md border border-primary bg-white p-2",
              )}
            >
              {ENVIRONMENTS.map((envOption) => (
                <Button
                  key={envOption}
                  color="primary"
                  id={`env_switch_${envOption}`}
                  isDisabled={isLoading}
                  size="sm"
                  variant={env === envOption ? "solid" : "light"}
                  onPress={() => navigate({ search: `?env=${envOption}` })}
                >
                  {capitaliseFirst(envOption)}
                </Button>
              ))}
            </div>
          )}

          {activateEnvSwitch && (
            <div className={classes.appSuggestion()}>
              {appOptions.map(({ href, label }) => (
                <Link
                  key={label}
                  color="primary-dark"
                  href={href}
                  id={label}
                  size="sm"
                >
                  {label}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
      <div ref={focusRef} className={classes.container()}>
        <div className={classes.content()}>
          <div className={classes.header()}>
            <Image
              alt="Recare logo"
              src={getStaticAsset(getRecareLogoWithNamePath("master"))}
              width={160}
            />
          </div>
          {onSubmit ? (
            <Form className={classes.form()} onSubmit={onSubmit}>
              {formContent}
            </Form>
          ) : (
            <div className={classes.form()}>{formContent}</div>
          )}
        </div>
        <div className={classes.footer()}>
          {isLogin && !isAcpApp(app) && (
            <div className={classes.footerLinks()}>
              <p>
                {translations.login.loginPage.contactInfo}{" "}
                <Link
                  aria-label={externalLinkLabel(
                    translations.login.loginPage.contactInfoLink,
                    "website",
                  )}
                  color="primary-dark"
                  href={contactUsUrl}
                  id="contact-us"
                  isExternal
                  size="sm"
                >
                  {translations.login.loginPage.contactInfoLink}
                </Link>
              </p>
              <p>
                {isSenderApp(app)
                  ? translations.login.loginPage.receiverInfo
                  : translations.login.loginPage.clinicInfo}{" "}
                <Link
                  aria-label={externalLinkLabel(
                    translations.login.clinic.clickHere,
                    "website",
                  )}
                  color="primary-dark"
                  href={otherAppUrl}
                  id="switch-app"
                  size="sm"
                >
                  {isSenderApp(app)
                    ? translations.login.loginPage.receiverInfoLink
                    : translations.login.loginPage.clinicLink}
                </Link>
              </p>
            </div>
          )}
          <hr className={classes.footerDivider()} />
          <p>
            {translations.login.loginPage.privacyPolicyInfo}{" "}
            <Link
              aria-label={externalLinkLabel(
                translations.login.privacyPolicy,
                "pdf",
              )}
              color="primary-dark"
              href={getDocumentUrl(DOCUMENT_PRIVACY_POLICY)}
              id="privacy-policy"
              isExternal
              size="sm"
            >
              {translations.login.privacyPolicy}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
